import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  Check,
  Copy,
  HelpCircle,
  Loader2,
  Package,
  Trash2,
  UserPlus,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CalendarioEvento, Calendario, Usuario } from '@/types';
import { calendarioEventoService } from '@/services/calendarioApi';
import { usuarioService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  agruparCalendariosPorEmpresa,
  empresaIdDoCalendario,
  temMaisDeUmaEmpresa,
} from '@/lib/calendarioEmpresas';
import {
  DEFAULT_RECURRENCE,
  RecurrenceEditor,
  RecurrenceState,
  buildRRuleString,
  parseRRuleString,
} from '@/components/calendario/RecurrenceEditor';

const STATUS_OPTIONS = [
  { value: 'confirmado', label: 'Confirmado' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'cancelado', label: 'Cancelado' },
] as const;

const PRIORIDADE_OPTIONS = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
] as const;

const REMINDER_ADD_OPTIONS = [
  { value: '0', label: 'Na hora do evento' },
  { value: '5', label: '5 minutos antes' },
  { value: '15', label: '15 minutos antes' },
  { value: '30', label: '30 minutos antes' },
  { value: '60', label: '1 hora antes' },
  { value: '120', label: '2 horas antes' },
  { value: '1440', label: '1 dia antes' },
  { value: '10080', label: '1 semana antes' },
] as const;

const MAX_LEMBRETES = 5;

function formatLembreteLabel(minutos: number): string {
  const found = REMINDER_ADD_OPTIONS.find((option) => Number(option.value) === minutos);
  if (found) return found.label;
  if (minutos % 1440 === 0) return `${minutos / 1440} dia(s) antes`;
  if (minutos % 60 === 0) return `${minutos / 60} hora(s) antes`;
  return `${minutos} minutos antes`;
}

const RSVP_STATUS_LABELS = {
  pendente: 'RSVP pendente',
  aceito: 'Aceito',
  recusado: 'Recusado',
  talvez: 'Talvez',
} as const;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  evento?: CalendarioEvento | null;
  calendarios: Calendario[];
  defaultStart?: string;
  defaultEnd?: string;
  defaultCalendarioId?: string;
  usuarios?: Pick<Usuario, 'id' | 'nome' | 'email'>[];
  /** Empresa a que pertence a lista `usuarios` (a ativa). Calendário de outra empresa usa os usuários dela. */
  usuariosEmpresaId?: string;
  /** Empresas em que a pessoa é admin — só nelas a lista de usuários é buscada (comum só vê quem tem acesso ao calendário). */
  empresasAdminIds?: string[];
  onSaved: () => void;
  /** Admin/superadmin tem permissão total — ignora filtro de acessos */
  isAdmin?: boolean;
  /** Força somente-leitura mesmo pra admin/superadmin — usado quando o evento é aberto via
   *  deep-link de outra tela (auditoria/visualização de outra pessoa), não edição própria. */
  forceReadOnly?: boolean;
}

type ParticipanteDraft = {
  tipo: 'usuario' | 'externo';
  usuario_id?: string;
  nome: string;
  email: string;
  rsvp_status?: 'nao_requer' | 'pendente' | 'aceito' | 'recusado' | 'talvez';
};

type RecurrenceActionScope = 'single' | 'series' | 'future';

function toDatetimeLocal(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Eventos "dia inteiro" são salvos como meia-noite UTC do dia correto (ver `buildPayload`,
 * que gera `new Date(dataInicio).toISOString()` a partir de um `<input type="date">`). Por
 * isso a leitura aqui precisa usar getters UTC — getters locais voltam um dia em fusos
 * negativos (Brasil, UTC-3), deslocando a data a cada vez que o evento é reaberto/editado.
 */
function toDateOnly(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/**
 * Parseia o valor de um <input type="date"|"datetime-local"> como horário LOCAL.
 * "YYYY-MM-DD" seria interpretado por `new Date()` como meia-noite UTC — em fusos negativos
 * (Brasil, UTC-3) isso volta um dia no `getDay()`/`getDate()` local, quebrando a recorrência.
 */
function parseInputDateLocal(value: string): Date | null {
  if (!value) return null;
  const [datePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

// Data fixa (quarta-feira) usada apenas para comparar duas RecurrenceState entre si — nunca
// exibida ao usuário nem persistida. Mantém a comparação estável independente da data do evento.
const RECURRENCE_COMPARISON_REFERENCE_DATE = new Date(2024, 0, 3);

function getParticipanteKey(participante: ParticipanteDraft): string {
  return participante.tipo === 'usuario'
    ? `usuario:${participante.usuario_id}`
    : `externo:${participante.email.toLowerCase()}`;
}

export function EventoModal({
  open,
  onOpenChange,
  evento,
  calendarios,
  defaultStart,
  defaultEnd,
  defaultCalendarioId,
  usuarios = [],
  usuariosEmpresaId,
  empresasAdminIds = [],
  onSaved,
  isAdmin = false,
  forceReadOnly = false,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [pendingScopeAction, setPendingScopeAction] = useState<'save' | 'delete' | null>(null);

  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [local, setLocal] = useState('');
  const [categoria, setCategoria] = useState('');
  const [status, setStatus] = useState<'confirmado' | 'pendente' | 'cancelado'>('confirmado');
  const [prioridade, setPrioridade] = useState<'baixa' | 'media' | 'alta'>('media');
  const [calendarioId, setCalendarioId] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [diaInteiro, setDiaInteiro] = useState(false);
  const [lembretesMinutos, setLembretesMinutos] = useState<number[]>([]);
  const [novoLembreteValor, setNovoLembreteValor] = useState('');
  const [recurrence, setRecurrence] = useState<RecurrenceState>({ ...DEFAULT_RECURRENCE });
  const [initialRecurrence, setInitialRecurrence] = useState<RecurrenceState>({ ...DEFAULT_RECURRENCE });
  const [participantes, setParticipantes] = useState<ParticipanteDraft[]>([]);
  const [fornecedor, setFornecedor] = useState('');
  const [pesoVeiculo, setPesoVeiculo] = useState('');
  const [volume, setVolume] = useState('');
  const [numeroNfe, setNumeroNfe] = useState('');
  const [novoParticipanteId, setNovoParticipanteId] = useState('');
  const [novoParticipanteNomeExterno, setNovoParticipanteNomeExterno] = useState('');
  const [novoParticipanteEmailExterno, setNovoParticipanteEmailExterno] = useState('');
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [myRsvpStatus, setMyRsvpStatus] = useState<'nao_requer' | 'pendente' | 'aceito' | 'recusado' | 'talvez' | null>(null);
  const [conflitos, setConflitos] = useState<Array<{ usuarioNome: string; titulos: string[] }>>([]);

  const { user } = useAuth();

  // Agenda única: `pode_editar` vem da API por calendário (papel da pessoa NA empresa dele). Sem ele
  // (superadmin, respostas antigas), vale a regra antiga: admin edita tudo; comum, só com acesso de edição.
  const podeEditarCalendario = (c: Calendario) =>
    typeof c.pode_editar === 'boolean'
      ? c.pode_editar
      : isAdmin || !c.acessos || c.acessos.some(a => a.pode_editar);
  const calendariosEditaveis = calendarios.filter(podeEditarCalendario);
  const agruparPorEmpresa = temMaisDeUmaEmpresa(calendariosEditaveis);
  const gruposCalendariosEditaveis = useMemo(
    () => agruparCalendariosPorEmpresa(calendariosEditaveis),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calendarios, isAdmin],
  );

  // Participantes vêm da EMPRESA DO CALENDÁRIO escolhido (não da empresa ativa).
  const calendarioEscolhido = calendarios.find((item) => item.id === calendarioId);
  const empresaDoCalendario = calendarioEscolhido ? empresaIdDoCalendario(calendarioEscolhido) : '';
  const [usuariosOutrasEmpresas, setUsuariosOutrasEmpresas] = useState<Record<string, Pick<Usuario, 'id' | 'nome' | 'email'>[]>>({});
  const empresaDoCalendarioEhOutra =
    !!empresaDoCalendario && !!usuariosEmpresaId && empresaDoCalendario !== usuariosEmpresaId;
  const buscarUsuariosDaEmpresa = empresaDoCalendarioEhOutra && empresasAdminIds.includes(empresaDoCalendario);

  useEffect(() => {
    if (!open || !buscarUsuariosDaEmpresa || usuariosOutrasEmpresas[empresaDoCalendario]) return;
    let cancelled = false;
    usuarioService.getByEmpresa(empresaDoCalendario).then((res) => {
      if (cancelled || !res.success) return;
      const lista = res.data.map((u) => ({ id: u.id, nome: u.nome, email: u.email }));
      setUsuariosOutrasEmpresas((atual) => ({ ...atual, [empresaDoCalendario]: lista }));
    });
    return () => {
      cancelled = true;
    };
  }, [open, buscarUsuariosDaEmpresa, empresaDoCalendario, usuariosOutrasEmpresas]);

  const usuariosDaEmpresa = useMemo(
    () => (empresaDoCalendarioEhOutra ? (usuariosOutrasEmpresas[empresaDoCalendario] ?? []) : usuarios),
    [empresaDoCalendarioEhOutra, usuariosOutrasEmpresas, empresaDoCalendario, usuarios],
  );

  const participantesDisponiveis = useMemo(() => {
    const fromUsers = usuariosDaEmpresa.map((usuario) => ({
      id: usuario.id,
      nome: usuario.nome,
      email: usuario.email,
    }));
    const calendariosDaEmpresa = empresaDoCalendario
      ? calendarios.filter((calendario) => !empresaIdDoCalendario(calendario) || empresaIdDoCalendario(calendario) === empresaDoCalendario)
      : calendarios;
    const fromCalendars = calendariosDaEmpresa.flatMap((calendario) =>
      (calendario.acessos ?? [])
        .map((acesso) => acesso.usuario)
        .filter((usuario): usuario is NonNullable<(typeof calendario.acessos)[number]['usuario']> => !!usuario)
        .map((usuario) => ({
          id: usuario.id,
          nome: usuario.nome,
          email: 'email' in usuario ? usuario.email : undefined,
        })),
    );
    const ownUser = evento?.criador ? [{
      id: evento.criador.id,
      nome: evento.criador.nome,
      email: undefined,
    }] : [];

    const unique = new Map<string, { id: string; nome: string; email?: string }>();
    [...fromUsers, ...fromCalendars, ...ownUser].forEach((usuario) => {
      unique.set(usuario.id, usuario);
    });
    return [...unique.values()];
  }, [usuariosDaEmpresa, calendarios, empresaDoCalendario, evento?.criador]);

  // Evento existente cujo calendário não é editável → modo somente-leitura. `forceReadOnly`
  // também vale pra admin/superadmin (auditoria/visualização de outra pessoa via deep-link).
  const calendarioDoEvento = evento ? calendarios.find(c => c.id === evento.calendario_id) : undefined;
  const isReadOnly =
    forceReadOnly ||
    (!!evento &&
      (typeof calendarioDoEvento?.pode_editar === 'boolean'
        ? !calendarioDoEvento.pode_editar
        : !isAdmin && !calendariosEditaveis.some(c => c.id === evento.calendario_id)));
  const isRecurringEvent = !!evento?.recorrencia_grupo_id;
  const isEntrega = calendarioEscolhido?.tipo === 'entregas';

  const startDateObj = parseInputDateLocal(dataInicio);
  // Compara as regras com uma data de referência FIXA (não a data real do evento): detecta
  // mudanças feitas pelo usuário nos controles de recorrência (incluindo troca de "dia do mês"
  // por "posição no mês", que depende do dia da semana) sem acusar mudança só porque a data do
  // evento em si foi alterada.
  const recurrenceChanged =
    buildRRuleString(recurrence, RECURRENCE_COMPARISON_REFERENCE_DATE) !==
    buildRRuleString(initialRecurrence, RECURRENCE_COMPARISON_REFERENCE_DATE);

  useEffect(() => {
    if (!open) return;

    if (evento) {
      setTitulo(evento.titulo || '');
      setDescricao(evento.descricao || '');
      setLocal(evento.local || '');
      setCategoria(evento.categoria || '');
      setStatus(evento.status || 'confirmado');
      setPrioridade(evento.prioridade || 'media');
      setCalendarioId(evento.calendario_id || '');
      const initialLembretes = evento.lembretes && evento.lembretes.length > 0
        ? evento.lembretes.map((lembrete) => lembrete.minutos_antes)
        : evento.lembrete_ativo && evento.lembrete_minutos_antes
          ? [evento.lembrete_minutos_antes]
          : [];
      setLembretesMinutos([...initialLembretes].sort((a, b) => a - b));
      const minhaParticipacao = (evento.participantes || []).find((p) => p.usuario_id === user?.id);
      setMyRsvpStatus(minhaParticipacao?.rsvp_status ?? null);
      // Recorrência: RRULE armazenada ou fallback do formato legado
      let recurrenceState: RecurrenceState;
      if (evento.recorrencia_rrule) {
        recurrenceState = parseRRuleString(evento.recorrencia_rrule);
      } else if (evento.recorrencia_grupo_id && evento.recorrencia_tipo && evento.recorrencia_tipo !== 'none') {
        recurrenceState = {
          ...DEFAULT_RECURRENCE,
          freq: evento.recorrencia_tipo,
          interval: evento.recorrencia_intervalo || 1,
          end: 'count',
          count: evento.recorrencia_total_ocorrencias || 2,
        };
      } else {
        recurrenceState = { ...DEFAULT_RECURRENCE };
      }
      setRecurrence(recurrenceState);
      setInitialRecurrence(recurrenceState);
      setFornecedor(evento.fornecedor || '');
      setPesoVeiculo(evento.peso_veiculo != null ? String(evento.peso_veiculo) : '');
      setVolume(evento.volume != null ? String(evento.volume) : '');
      setNumeroNfe(evento.numero_nfe || '');
      setParticipantes(
        (evento.participantes || []).map((participante) => ({
          tipo: participante.usuario_id ? 'usuario' : 'externo',
          usuario_id: participante.usuario_id || undefined,
          nome: participante.usuario?.nome || participante.nome_externo || 'Convidado externo',
          email: participante.usuario?.email || participante.email_externo || '',
          rsvp_status: participante.rsvp_status || 'nao_requer',
        })),
      );
      setDiaInteiro(evento.dia_inteiro || false);
      if (evento.dia_inteiro) {
        setDataInicio(toDateOnly(evento.data_inicio));
        setDataFim(toDateOnly(evento.data_fim));
      } else {
        setDataInicio(toDatetimeLocal(evento.data_inicio));
        setDataFim(toDatetimeLocal(evento.data_fim));
      }
    } else {
      setTitulo('');
      setDescricao('');
      setLocal('');
      setCategoria('');
      setStatus('confirmado');
      setPrioridade('media');
      setCalendarioId(defaultCalendarioId || calendariosEditaveis[0]?.id || '');
      setLembretesMinutos([]);
      setMyRsvpStatus(null);
      setRecurrence({ ...DEFAULT_RECURRENCE });
      setInitialRecurrence({ ...DEFAULT_RECURRENCE });
      setFornecedor('');
      setPesoVeiculo('');
      setVolume('');
      setNumeroNfe('');
      setParticipantes([]);
      setDiaInteiro(false);
      setDataInicio(defaultStart ? toDatetimeLocal(defaultStart) : '');
      setDataFim(defaultEnd ? toDatetimeLocal(defaultEnd) : '');
    }
    setNovoParticipanteId('');
    setNovoParticipanteNomeExterno('');
    setNovoParticipanteEmailExterno('');
  }, [open, evento, user?.id]);

  const renderCalendarioOption = (c: Calendario) => (
    <SelectItem key={c.id} value={c.id}>
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-3 w-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: c.cor }}
        />
        {c.nome}
      </div>
    </SelectItem>
  );

  // Trocar para um calendário de OUTRA empresa: convidados internos da empresa anterior saem
  // (a lista de participantes passa a ser a da empresa do novo calendário). Externos ficam.
  const handleTrocarCalendario = (novoId: string) => {
    const novo = calendarios.find((item) => item.id === novoId);
    const novaEmpresa = novo ? empresaIdDoCalendario(novo) : '';
    setCalendarioId(novoId);
    if (!novaEmpresa || !empresaDoCalendario || novaEmpresa === empresaDoCalendario) return;
    if (participantes.some((participante) => participante.tipo === 'usuario')) {
      setParticipantes((atual) => atual.filter((participante) => participante.tipo !== 'usuario'));
      toast.info('Os convidados internos foram removidos: eles pertencem à empresa do calendário anterior.');
    }
  };

  const handleAddParticipante = () => {
    if (!novoParticipanteId) return;
    const usuario = participantesDisponiveis.find((item) => item.id === novoParticipanteId);
    if (!usuario) return;

    if (participantes.some((participante) => participante.usuario_id === novoParticipanteId)) {
      toast.error('Esse participante já foi adicionado');
      return;
    }

    setParticipantes((current) => [
      ...current,
      {
        tipo: 'usuario',
        usuario_id: usuario.id,
        nome: usuario.nome,
        email: usuario.email || '',
      },
    ]);
    setNovoParticipanteId('');
    setNovoParticipanteNomeExterno('');
    setNovoParticipanteEmailExterno('');
  };

  const handleAddParticipanteExterno = () => {
    const email = novoParticipanteEmailExterno.trim().toLowerCase();
    const nome = novoParticipanteNomeExterno.trim();

    if (!email) {
      toast.error('Informe o e-mail do participante externo');
      return;
    }

    if (!isValidEmail(email)) {
      toast.error('Informe um e-mail externo valido');
      return;
    }

    if (participantes.some((participante) => participante.email.toLowerCase() === email)) {
      toast.error('Esse participante ja foi adicionado');
      return;
    }

    setParticipantes((current) => [
      ...current,
      {
        tipo: 'externo',
        nome: nome || email,
        email,
      },
    ]);
    setNovoParticipanteNomeExterno('');
    setNovoParticipanteEmailExterno('');
  };

  const handleRemoveParticipante = (participantKey: string) => {
    setParticipantes((current) => current.filter((participante) => getParticipanteKey(participante) !== participantKey));
  };

  const handleAddLembrete = () => {
    if (!novoLembreteValor) return;
    const minutos = Number(novoLembreteValor);

    if (lembretesMinutos.includes(minutos)) {
      toast.error('Esse lembrete já foi adicionado');
      return;
    }
    if (lembretesMinutos.length >= MAX_LEMBRETES) {
      toast.error(`No máximo ${MAX_LEMBRETES} lembretes por evento`);
      return;
    }

    setLembretesMinutos((current) => [...current, minutos].sort((a, b) => a - b));
    setNovoLembreteValor('');
  };

  const handleRemoveLembrete = (minutos: number) => {
    setLembretesMinutos((current) => current.filter((m) => m !== minutos));
  };

  const handleRsvp = async (resposta: 'aceito' | 'recusado' | 'talvez') => {
    if (!evento || rsvpLoading) return;
    setRsvpLoading(true);
    try {
      const res = await calendarioEventoService.respondRsvp(evento.calendario_id, evento.id, resposta);
      if (!res.success) throw new Error(res.error);
      setMyRsvpStatus(resposta);
      toast.success('Resposta registrada!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao registrar resposta');
    } finally {
      setRsvpLoading(false);
    }
  };

  // Verifica conflitos de agenda dos convidados internos ao alterar data/participantes
  useEffect(() => {
    if (!open || isReadOnly) {
      setConflitos([]);
      return;
    }

    const internalIds = participantes
      .filter((participante) => participante.tipo === 'usuario' && participante.usuario_id)
      .map((participante) => participante.usuario_id!);

    if (internalIds.length === 0 || !dataInicio) {
      setConflitos([]);
      return;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      const dataInicioIso = new Date(dataInicio).toISOString();
      const dataFimIso = dataFim ? new Date(dataFim).toISOString() : undefined;

      const res = await calendarioEventoService.getConflitos({
        usuarioIds: internalIds,
        dataInicio: dataInicioIso,
        dataFim: dataFimIso,
        excluirEventoId: evento?.id,
        calendarioId: calendarioId || undefined,
      });
      if (cancelled || !res.success) return;

      const nomesPorId = new Map(participantesDisponiveis.map((item) => [item.id, item.nome]));
      const mapeado = res.data
        .filter((item) => item.eventos.length > 0)
        .map((item) => ({
          usuarioNome: nomesPorId.get(item.usuario_id) || 'Participante',
          // Evento de outra empresa chega anonimizado: nunca mostrar título/detalhes dele.
          titulos: Array.from(new Set(
            item.eventos.map((ev) => (ev.oculto || !ev.titulo ? 'Ocupado (outra empresa)' : ev.titulo)),
          )),
        }));
      setConflitos(mapeado);
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [open, isReadOnly, participantes, dataInicio, dataFim, evento?.id, calendarioId, participantesDisponiveis]);

  const buildPayload = () => ({
    titulo: titulo.trim(),
    descricao: descricao || undefined,
    local: local || undefined,
    categoria: categoria || undefined,
    status,
    prioridade,
    lembretes: lembretesMinutos,
    // Criação: sempre envia a regra. Edição: só quando o usuário mexeu na
    // recorrência (null remove; string regenera a série no backend).
    recorrencia_rrule: !evento
      ? buildRRuleString(recurrence, startDateObj) ?? undefined
      : recurrenceChanged
        ? buildRRuleString(recurrence, startDateObj)
        : undefined,
    participantes: participantes.map((participante) => ({
      usuario_id: participante.tipo === 'usuario' ? participante.usuario_id : undefined,
      nome: participante.tipo === 'externo' ? participante.nome : undefined,
      email: participante.email,
      papel: 'convidado' as const,
    })),
    data_inicio: new Date(dataInicio).toISOString(),
    data_fim: dataFim ? new Date(dataFim).toISOString() : undefined,
    dia_inteiro: diaInteiro,
    fornecedor: isEntrega ? fornecedor.trim() : undefined,
    numero_nfe: isEntrega ? numeroNfe.trim() : undefined,
    peso_veiculo: isEntrega ? (pesoVeiculo.trim() === '' ? null : Number(pesoVeiculo)) : undefined,
    volume: isEntrega ? (volume.trim() === '' ? null : Number(volume)) : undefined,
  });

  const validateForm = () => {
    if (!titulo.trim()) {
      toast.error('Informe o título do evento');
      return false;
    }
    if (!calendarioId) {
      toast.error('Selecione um calendário');
      return false;
    }
    if (!dataInicio) {
      toast.error('Informe a data de início');
      return false;
    }
    if (isEntrega) {
      if (!fornecedor.trim()) {
        toast.error('Informe o fornecedor da entrega');
        return false;
      }
      if (!numeroNfe.trim()) {
        toast.error('Informe o número da NFe');
        return false;
      }
      if (pesoVeiculo.trim() !== '' && (Number.isNaN(Number(pesoVeiculo)) || Number(pesoVeiculo) < 0)) {
        toast.error('Peso do veículo deve ser um número válido');
        return false;
      }
      if (volume.trim() !== '' && (Number.isNaN(Number(volume)) || Number(volume) < 0)) {
        toast.error('Volume deve ser um número válido');
        return false;
      }
    }
    if (recurrence.freq !== 'none') {
      if (recurrence.end === 'count' && (!Number.isInteger(recurrence.count) || recurrence.count < 2 || recurrence.count > 366)) {
        toast.error('Informe entre 2 e 366 ocorrências para a recorrência');
        return false;
      }
      if (recurrence.end === 'until') {
        if (!recurrence.until) {
          toast.error('Informe a data final da recorrência');
          return false;
        }
        if (dataInicio && new Date(`${recurrence.until}T23:59:59`) <= new Date(dataInicio)) {
          toast.error('A data final da recorrência deve ser posterior ao início do evento');
          return false;
        }
      }
    }
    return true;
  };

  const executeSave = async (scope: RecurrenceActionScope = 'single') => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = buildPayload();

      if (evento) {
        const res = await calendarioEventoService.update(evento.calendario_id, evento.id, {
          ...payload,
          calendario_id: calendarioId,
        }, { scope });
        if (!res.success) throw new Error(res.error);
        toast.success(
          scope === 'series'
            ? 'Série atualizada!'
            : scope === 'future'
              ? 'Esta e as próximas ocorrências atualizadas!'
              : isRecurringEvent
                ? 'Ocorrência atualizada e desanexada da série!'
                : recurrenceChanged && recurrence.freq !== 'none'
                  ? 'Série criada a partir do evento!'
                  : 'Evento atualizado!',
        );
      } else {
        const res = await calendarioEventoService.create(calendarioId, payload);
        if (!res.success) throw new Error(res.error);
        toast.success(
          recurrence.freq !== 'none' ? 'Série recorrente criada!' : 'Evento criado!',
        );
      }

      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar evento');
    } finally {
      setLoading(false);
      setScopeDialogOpen(false);
      setPendingScopeAction(null);
    }
  };

  const handleSave = async () => {
    if (evento && isRecurringEvent) {
      if (!validateForm()) return;
      setPendingScopeAction('save');
      setScopeDialogOpen(true);
      return;
    }

    await executeSave('single');
  };

  const executeDelete = async (scope: RecurrenceActionScope = 'single') => {
    if (!evento) return;
    setLoading(true);
    try {
      const res = await calendarioEventoService.remove(evento.calendario_id, evento.id, { scope });
      if (!res.success) throw new Error(res.error);
      toast.success(
        scope === 'series'
          ? 'Série excluída!'
          : scope === 'future'
            ? 'Esta e as próximas ocorrências excluídas!'
            : isRecurringEvent
              ? 'Ocorrência cancelada!'
              : 'Evento excluído!',
      );
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir evento');
    } finally {
      setLoading(false);
      setDeleteOpen(false);
      setScopeDialogOpen(false);
      setPendingScopeAction(null);
    }
  };

  const handleDelete = async () => {
    if (isRecurringEvent) {
      setDeleteOpen(false);
      setPendingScopeAction('delete');
      setScopeDialogOpen(true);
      return;
    }

    await executeDelete('single');
  };

  const handleRestore = async () => {
    if (!evento) return;
    setLoading(true);
    try {
      const res = await calendarioEventoService.restore(evento.calendario_id, evento.id);
      if (!res.success) throw new Error(res.error);
      toast.success('Ocorrência restaurada!');
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao restaurar evento');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async () => {
    if (!evento || loading) return;
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Cópia é sempre um evento independente, nunca uma nova série
      const { recorrencia_rrule: _recorrenciaRrule, ...payloadSemRecorrencia } = buildPayload();
      const res = await calendarioEventoService.create(calendarioId, {
        ...payloadSemRecorrencia,
        titulo: `${payloadSemRecorrencia.titulo} (cópia)`,
      });
      if (!res.success) throw new Error(res.error);
      toast.success('Evento duplicado!');
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao duplicar evento');
    } finally {
      setLoading(false);
    }
  };

  const handleScopeChoice = async (scope: RecurrenceActionScope) => {
    if (pendingScopeAction === 'save') {
      await executeSave(scope);
      return;
    }

    if (pendingScopeAction === 'delete') {
      await executeDelete(scope);
    }
  };

  const calendarioSelecionado = calendarios.find((item) => item.id === calendarioId);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="overflow-hidden border-0 p-0 shadow-2xl sm:max-w-2xl">
          <DialogHeader className="border-b px-6 py-5">
            <div className="flex items-start gap-4">
              <div
                className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border shadow-sm"
                style={{
                  backgroundColor: `${calendarioSelecionado?.cor || '#10B981'}18`,
                  color: calendarioSelecionado?.cor || '#10B981',
                  borderColor: `${calendarioSelecionado?.cor || '#10B981'}45`,
                }}
              >
                <CalendarClock className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-xl">
                  {evento ? (isReadOnly ? 'Visualizar evento' : 'Editar evento') : 'Novo evento'}
                </DialogTitle>
                <DialogDescription className="sr-only">Formulário do evento</DialogDescription>
                <div className="mt-3 flex flex-wrap gap-2">
                  {isReadOnly && (
                    <Badge variant="outline" className="rounded-full bg-muted/70">
                      somente leitura
                    </Badge>
                  )}
                  {evento?.recorrencia_excecao_tipo === 'detached' && (
                    <Badge variant="outline" className="rounded-full border-amber-200 bg-amber-50 text-amber-700">
                      ocorrência desanexada da série
                    </Badge>
                  )}
                  {evento?.recorrencia_excecao_tipo === 'cancelled' && (
                    <Badge variant="outline" className="rounded-full border-rose-200 bg-rose-50 text-rose-700">
                      ocorrência cancelada
                    </Badge>
                  )}
                  {(evento?.recorrencia_excecao_tipo === 'none' || !evento?.recorrencia_excecao_tipo) && evento?.recorrencia_grupo_id && (
                    <Badge variant="outline" className="rounded-full bg-muted/70">
                      faz parte de uma série recorrente
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </DialogHeader>

          {evento && myRsvpStatus && myRsvpStatus !== 'nao_requer' && (
            <div className="flex flex-wrap items-center gap-2 border-b bg-muted/20 px-6 py-3">
              <span className="text-sm text-muted-foreground">Sua presença neste evento:</span>
              <Button
                type="button"
                size="sm"
                variant={myRsvpStatus === 'aceito' ? 'default' : 'outline'}
                onClick={() => handleRsvp('aceito')}
                disabled={rsvpLoading}
              >
                <Check className="mr-1 h-3.5 w-3.5" />
                Aceitar
              </Button>
              <Button
                type="button"
                size="sm"
                variant={myRsvpStatus === 'talvez' ? 'default' : 'outline'}
                onClick={() => handleRsvp('talvez')}
                disabled={rsvpLoading}
              >
                <HelpCircle className="mr-1 h-3.5 w-3.5" />
                Talvez
              </Button>
              <Button
                type="button"
                size="sm"
                variant={myRsvpStatus === 'recusado' ? 'default' : 'outline'}
                onClick={() => handleRsvp('recusado')}
                disabled={rsvpLoading}
              >
                <XCircle className="mr-1 h-3.5 w-3.5" />
                Recusar
              </Button>
            </div>
          )}

          <div className="max-h-[68vh] overflow-y-auto">
            <div className="space-y-5 px-6 py-5">
              <div className="space-y-2">
                <Label htmlFor="titulo">Título *</Label>
                <Input
                  id="titulo"
                  placeholder="Ex: Reunião semanal, alinhamento comercial ou entrega do projeto"
                  value={titulo}
                  onChange={e => setTitulo(e.target.value)}
                  autoFocus
                  disabled={isReadOnly}
                  className="h-11"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Calendário *</Label>
                  <Select value={calendarioId} onValueChange={handleTrocarCalendario}>
                    <SelectTrigger disabled={isReadOnly} className="h-11">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {agruparPorEmpresa
                        ? gruposCalendariosEditaveis.map((grupo) => (
                            <SelectGroup key={grupo.empresaId}>
                              <SelectLabel>{grupo.empresaNome}</SelectLabel>
                              {grupo.calendarios.map(renderCalendarioOption)}
                            </SelectGroup>
                          ))
                        : calendariosEditaveis.map(renderCalendarioOption)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="local">Local</Label>
                  <Input
                    id="local"
                    placeholder="Sala, Google Meet ou endereço"
                    value={local}
                    onChange={e => setLocal(e.target.value)}
                    disabled={isReadOnly}
                    className="h-11"
                  />
                </div>
              </div>

              <div className="rounded-2xl border bg-muted/15 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Data e horário</div>
                    <p className="text-xs text-muted-foreground">
                      Defina o período do compromisso e se ele ocupa o dia inteiro.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 rounded-full border bg-background px-3 py-1.5">
                    <Switch
                      id="dia-inteiro"
                      checked={diaInteiro}
                      onCheckedChange={v => {
                        setDiaInteiro(v);
                        setDataInicio('');
                        setDataFim('');
                      }}
                      disabled={isReadOnly}
                    />
                    <Label htmlFor="dia-inteiro" className="cursor-pointer text-xs whitespace-nowrap">
                      Dia inteiro
                    </Label>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="data-inicio">Início *</Label>
                    <Input
                      id="data-inicio"
                      type={diaInteiro ? 'date' : 'datetime-local'}
                      value={dataInicio}
                      onChange={e => setDataInicio(e.target.value)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="data-fim">Fim</Label>
                    <Input
                      id="data-fim"
                      type={diaInteiro ? 'date' : 'datetime-local'}
                      value={dataFim}
                      onChange={e => setDataFim(e.target.value)}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>

              {conflitos.length > 0 && (
                <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-1">
                    <p className="font-medium">Possível conflito de agenda</p>
                    {conflitos.map((conflito) => (
                      <p key={conflito.usuarioNome}>
                        {conflito.usuarioNome} já tem: {conflito.titulos.join(', ')}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {isEntrega && (
                <div className="space-y-3 rounded-2xl border border-amber-200 bg-amber-50/40 p-4">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-amber-600" />
                    <Label className="font-medium">Dados da entrega</Label>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="fornecedor">Fornecedor *</Label>
                      <Input
                        id="fornecedor"
                        placeholder="Nome do fornecedor"
                        value={fornecedor}
                        onChange={e => setFornecedor(e.target.value)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="numero-nfe">Número da NFe *</Label>
                      <Input
                        id="numero-nfe"
                        placeholder="Ex: 123456"
                        value={numeroNfe}
                        onChange={e => setNumeroNfe(e.target.value)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="peso-veiculo">Peso do veículo (kg)</Label>
                      <Input
                        id="peso-veiculo"
                        type="number"
                        min={0}
                        step="0.001"
                        placeholder="0"
                        value={pesoVeiculo}
                        onChange={e => setPesoVeiculo(e.target.value)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="volume">Volume (m³)</Label>
                      <Input
                        id="volume"
                        type="number"
                        min={0}
                        step="0.001"
                        placeholder="0"
                        value={volume}
                        onChange={e => setVolume(e.target.value)}
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="categoria">Categoria</Label>
                  <Input
                    id="categoria"
                    placeholder="Ex: Reunião, Entrega ou Feriado"
                    value={categoria}
                    onChange={e => setCategoria(e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
                    <SelectTrigger disabled={isReadOnly}>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Prioridade</Label>
                <Select value={prioridade} onValueChange={(value) => setPrioridade(value as typeof prioridade)}>
                  <SelectTrigger disabled={isReadOnly} className="md:w-1/2">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORIDADE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 rounded-2xl border bg-muted/15 p-4">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-muted-foreground" />
                  <Label>Lembretes</Label>
                </div>

                {lembretesMinutos.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {lembretesMinutos.map((minutos) => (
                      <Badge key={minutos} variant="secondary" className="gap-1 pr-1">
                        {formatLembreteLabel(minutos)}
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleRemoveLembrete(minutos)}
                            className="ml-1 rounded-full p-0.5 hover:bg-muted-foreground/20"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                )}

                {!isReadOnly && (
                  <div className="flex gap-2">
                    <Select value={novoLembreteValor} onValueChange={setNovoLembreteValor}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Adicionar lembrete" />
                      </SelectTrigger>
                      <SelectContent>
                        {REMINDER_ADD_OPTIONS
                          .filter((option) => !lembretesMinutos.includes(Number(option.value)))
                          .map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="secondary" size="sm" onClick={handleAddLembrete} disabled={!novoLembreteValor}>
                      Adicionar
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2 rounded-2xl border bg-muted/15 p-4">
                <Label>Repetição</Label>
                <RecurrenceEditor
                  value={recurrence}
                  onChange={setRecurrence}
                  startDate={startDateObj}
                  disabled={isReadOnly}
                />
                {evento && isRecurringEvent && recurrenceChanged && (
                  <p className="text-xs text-amber-600">
                    Alterar a repetição regenera as ocorrências da série — exceções e ocorrências canceladas são descartadas.
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <Label>Convidados</Label>
                </div>

                {participantes.length > 0 ? (
                  <div className="space-y-2 rounded-2xl border p-3">
                    {participantes.map((participante) => (
                      <div key={getParticipanteKey(participante)} className="flex items-center justify-between gap-2 rounded-xl bg-muted/20 px-3 py-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium">{participante.nome}</span>
                            <Badge variant="secondary">Convidado</Badge>
                            <Badge variant="outline">
                              {participante.tipo === 'usuario' ? 'Usuário do sistema' : 'Externo'}
                            </Badge>
                            {participante.rsvp_status && participante.rsvp_status !== 'nao_requer' && (
                              <Badge variant="outline">
                                {RSVP_STATUS_LABELS[participante.rsvp_status]}
                              </Badge>
                            )}
                          </div>
                          <p className="truncate text-xs text-muted-foreground">{participante.email}</p>
                        </div>
                        {!isReadOnly && (
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              title="Remover participante"
                              onClick={() => handleRemoveParticipante(getParticipanteKey(participante))}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed bg-muted/20 px-4 py-5 text-sm text-muted-foreground">
                    Nenhum participante adicionado.
                  </div>
                )}

                {!isReadOnly && (
                  <div className="space-y-3 rounded-2xl border p-3">
                    <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
                      <Select value={novoParticipanteId} onValueChange={setNovoParticipanteId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Adicionar convidado interno" />
                        </SelectTrigger>
                        <SelectContent>
                          {participantesDisponiveis
                            .filter((usuario) => !participantes.some((participante) => participante.usuario_id === usuario.id))
                            .map((usuario) => (
                              <SelectItem key={usuario.id} value={usuario.id}>
                                {usuario.nome}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="secondary" onClick={handleAddParticipante} disabled={!novoParticipanteId}>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Interno
                      </Button>
                    </div>

                    <div className="grid gap-2 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)_auto]">
                      <Input
                        placeholder="Nome externo (opcional)"
                        value={novoParticipanteNomeExterno}
                        onChange={(e) => setNovoParticipanteNomeExterno(e.target.value)}
                      />
                      <Input
                        placeholder="email@externo.com"
                        type="email"
                        value={novoParticipanteEmailExterno}
                        onChange={(e) => setNovoParticipanteEmailExterno(e.target.value)}
                      />
                      <Button type="button" variant="outline" onClick={handleAddParticipanteExterno} disabled={!novoParticipanteEmailExterno.trim()}>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Externo
                      </Button>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      Participantes externos entram por e-mail e aparecem no evento mesmo sem acesso ao sistema.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  placeholder="Adicione contexto, pauta ou observações importantes."
                  rows={4}
                  value={descricao}
                  onChange={e => setDescricao(e.target.value)}
                  disabled={isReadOnly}
                  className="resize-none"
                />
              </div>
            </div>

          </div>

          <DialogFooter className="gap-2 border-t px-6 py-4">
            {evento && !isReadOnly && (
              <Button
                variant="ghost"
                size="sm"
                className="mr-auto text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Excluir
              </Button>
            )}
            {evento?.recorrencia_excecao_tipo === 'cancelled' && !isReadOnly && (
              <Button
                variant="secondary"
                size="sm"
                className="mr-auto"
                onClick={handleRestore}
                disabled={loading}
              >
                Restaurar ocorrência
              </Button>
            )}
            {evento && !isReadOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDuplicate}
                disabled={loading}
              >
                <Copy className="h-4 w-4 mr-1" />
                Duplicar
              </Button>
            )}
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
            {!isReadOnly && (
              <Button onClick={handleSave} disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir evento?</AlertDialogTitle>
            <AlertDialogDescription>
              O evento <strong>{evento?.titulo}</strong> será excluído permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={scopeDialogOpen}
        onOpenChange={(nextOpen) => {
          setScopeDialogOpen(nextOpen);
          if (!nextOpen) setPendingScopeAction(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pendingScopeAction === 'delete' ? 'Aplicar exclusão onde?' : 'Aplicar alteração onde?'}
            </DialogTitle>
            <DialogDescription>
              Este evento faz parte de uma série recorrente. Você pode agir só nesta ocorrência ou na série inteira.
            </DialogDescription>
          </DialogHeader>
          {pendingScopeAction === 'save' && !recurrenceChanged && (
            <p className="text-sm text-muted-foreground">
              Ao alterar somente esta ocorrência, ela será desanexada da série para virar um evento independente.
            </p>
          )}
          {pendingScopeAction === 'save' && recurrenceChanged && (
            <p className="text-sm text-muted-foreground">
              Você alterou a repetição — a mudança pode ser aplicada à série inteira ou desta ocorrência em diante.
            </p>
          )}
          {pendingScopeAction === 'delete' && (
            <p className="text-sm text-muted-foreground">
              Ao excluir somente esta ocorrência, ela será marcada como cancelada e o restante da série continuará ativo.
            </p>
          )}
          <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch sm:justify-start">
            {!(pendingScopeAction === 'save' && recurrenceChanged) && (
              <Button
                type="button"
                variant="outline"
                onClick={() => handleScopeChoice('single')}
                disabled={loading}
              >
                {pendingScopeAction === 'delete' ? 'Cancelar somente esta ocorrência' : 'Somente este evento'}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => handleScopeChoice('future')}
              disabled={loading}
            >
              {pendingScopeAction === 'delete' ? 'Excluir este e os futuros' : 'Este e os futuros'}
            </Button>
            <Button
              type="button"
              onClick={() => handleScopeChoice('series')}
              disabled={loading}
            >
              {pendingScopeAction === 'delete' ? 'Excluir toda a série' : 'Atualizar toda a série'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
